import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import ButtonNB from '../ui-nb/ButtonNB';
import CardNB from '../ui-nb/CardNB';
import IconNB from '../ui-nb/IconNB';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../ToastNB';

interface Comment {
  id: string;
  order_id: string;
  user_id: string;
  content: string;
  created_at: string;
  profiles?: { id: string; full_name: string } | null;
}

interface Props {
  orderId: string;
}

export default function OrderCommentThread({ orderId }: Props) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const user = useAuthStore(s => s.user);
  const { addToast } = useToast();

  const fetchComments = async () => {
    const { data } = await supabase
      .from('order_comments')
      .select('*, profiles:user_id(id, full_name)')
      .eq('order_id', orderId)
      .order('created_at', { ascending: true });

    if (data) setComments(data as Comment[]);
    setLoading(false);
  };

  useEffect(() => {
    fetchComments();
  }, [orderId]);

  const handleSubmit = async () => {
    if (!newComment.trim() || !user?.id) return;
    setSubmitting(true);

    const { error } = await supabase.from('order_comments').insert({
      order_id: orderId,
      user_id: user.id,
      content: newComment.trim(),
    });

    setSubmitting(false);

    if (error) {
      addToast('Failed to add comment', 'error');
      return;
    }

    setNewComment('');
    fetchComments();
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <CardNB className="p-6">
      <h2 className="text-sm font-nb font-bold text-nb-text-muted uppercase mb-3">
        <span className="inline-flex items-center gap-2">
          <IconNB name="message" size={16} />
          Comments
        </span>
      </h2>

      {loading ? (
        <p className="text-sm text-nb-text-muted">Loading comments...</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-nb-text-muted">No comments yet</p>
      ) : (
        <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
          {comments.map(comment => (
            <div key={comment.id} className="border-l-3 border-nb-yellow pl-3">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-nb font-bold text-nb-black">
                  {comment.profiles?.full_name || 'Unknown'}
                </span>
                <span className="text-xs text-nb-text-muted">
                  {formatDate(comment.created_at)}
                </span>
              </div>
              <p className="text-sm text-nb-black whitespace-pre-wrap">{comment.content}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          value={newComment}
          onChange={e => setNewComment(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder="Add a comment..."
          disabled={submitting}
          className="flex-1 px-3 py-2 bg-white border-2 border-nb-black text-black text-sm focus:outline-none focus:border-nb-yellow rounded-[2px]"
        />
        <ButtonNB
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          loading={submitting}
          disabled={!newComment.trim()}
        >
          <IconNB name="plus" size={14} />
          Add
        </ButtonNB>
      </div>
    </CardNB>
  );
}
